import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ForgotResetPasswordService } from "../../core/services/forgot-reset-password.service.service";
import { NurseService } from "../../core/services/nurse.service";
import { INurse } from "../../model/interface/nurse";
import { InvoiceService } from "../../core/services/invoice.service";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { loadListState, saveListState } from "../../shared/utils/list-state";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

@Component({
  selector: 'app-invoice',
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiCell,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiStringifyContentPipe,
    TuiLoader,
    TuiDataListDropdownManager,
  ],
  templateUrl: './invoice.component.html',
  styleUrl: './invoice.component.scss'
})
export class InvoiceComponent {
  protected readonly breadcrumbs = ["Royal Hospital", "Invoice"];
    protected readonly sizes = ["m"] as const;
    protected open = false;
    protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
    protected openDropdown = false;
    protected size = this.sizes[0];
    isLoading = false;
    invoiceData: any;
    paginatedData: INurse[] = [];
    invoiceService = inject(InvoiceService);
    showResults: string = "";
    filters = {
      searchText: "",
      status: null,
    };
    pageIndex = 1;
    pageSize = 10;
    total: number = 6;
    sortField = "createdAt";
    sortOrder: number = -1;
    openMap: { [nurseId: string]: boolean } = {};
    /** Multi-select bulk-action state. Keyed by invoice _id. */
    selectedIds = new Set<string>();
    bulkBusy = false;
  
    constructor(
      private datePipe: DatePipe,
      private resetPasswordService: ForgotResetPasswordService,
      private alerts: TuiAlertService
    ) {}
  
    // Form setup
    protected readonly form = new FormGroup({
      search: new FormControl(""),
      select: new FormControl(""),
    });
  
    protected value = null;
  
    protected readonly items = [
      { status: "Draft", value: "Draft" },
      { status: "Sent", value: "Sent" },
    ];
  
    protected stringify(item: { status: string; value: boolean } | null): string {
      if (!item) return "";
      return item.status;
    }
  
    private readonly listStateKey = "invoice-list";

    ngOnInit(): void {
      this.restoreListState();
      this.loadInvoices();
    }

    private restoreListState(): void {
      const restored = loadListState(this.listStateKey, {
        filters: this.filters,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      });
      this.filters = { ...this.filters, ...restored.filters };
      this.pageIndex = restored.pageIndex;
      this.pageSize = restored.pageSize;
      this.sortField = restored.sortField;
      this.sortOrder = restored.sortOrder;
    }

    private rememberListState(): void {
      saveListState(this.listStateKey, {
        filters: this.filters,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      });
    }
  
    loadInvoices(): void {
      this.rememberListState();
      this.selectedIds = new Set<string>();
      const payload = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        filters: {
          ...this.filters,
          status: this.filters.status ? (this.filters.status as { value: string }).value : null,
        },
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      };
      
  
      this.isLoading = true;
  
      this.invoiceService.getPagedInvoices(payload).subscribe({
        next: (res: any) => {
          if (res && res.length > 0 && res[0].data) {
            this.invoiceData = res[0].data;
            this.total = res[0].metadata[0].total;
          } else {
            this.invoiceData = [];
            this.total = 0;
          }
        },
        error: (error) => {
          console.error("Error fetching client data:", error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
    }
  
    getInvoiceStatusBgColor(status: string): string {
      switch (status) {
        case 'Draft':
          return 'purple';
        case 'Sent':
          return 'blue';
        default:
          return 'gray';
      }
    }

    toggleStatus(invoiceId: string): void {
      this.isLoading = true; 
      this.invoiceService.getOneInvoiceById(invoiceId).subscribe({
        next: (invoice: any) => {
          if (!invoice) {
            this.isLoading = false;
            return;
          }
          const newStatus = invoice.status === 'Draft' ? 'Sent' : 'Draft';
          const updatedInvoice = { ...invoice, status: newStatus };
    
          this.invoiceService.updateInvoice(updatedInvoice).subscribe({
            next: (response) => {
              this.alerts
                .open(`Invoice has been updated successfully.`, {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.loadInvoices();
              this.isLoading = false;
            },
            error: (error) => {
              console.error("Error updating invoice data:", error);
              this.alerts
                .open("Failed to update invoice data.", {
                  label: "Error",
                  appearance: "error",
                })
                .subscribe();
              this.isLoading = false;
            },
          });
        },
        error: (error) => {
          console.error("Error fetching invoice data:", error);
          this.isLoading = false;
        },
      });
    }
    
  
    toggleDropdown(clientId: string): void {
      this.openMap[clientId] = !this.openMap[clientId];
    }
  
    sort(): void {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
      this.loadInvoices();
    }
  
    applyFilters(newFilters: any): void {
      this.filters = { ...this.filters, ...newFilters };
      this.loadInvoices();
    }
   
  
    applySort(): void {
      this.loadInvoices();
    }
  
    onEnter(event: KeyboardEvent): void {
      if (event.key === "Enter") {
        this.applyFilters({ searchText: this.filters.searchText });
      }
    }
  
    onPageChange(pageIndex: number): void {
      this.pageIndex = pageIndex;
      this.loadInvoices();
    }
  
    onPageSizeChange(pageSize: number): void {
      this.pageSize = Number(pageSize);
      this.pageIndex = 1;
      this.loadInvoices();
    }
  
  
    clearSearch(): void {
      this.filters.searchText = "";
      this.filters.status = null;
      this.applyFilters({ searchText: this.filters.searchText, status: null });
    }
  
    getPageCount(): number {
      return Math.ceil(this.total / this.pageSize);
    }

    invoiceClientDisplayRef(client: any): string {
      return formatClientIdentifier(client);
    }

    /* ---------- Multi-select bulk actions ---------- */

    isSelected(invoiceId: string): boolean {
      return this.selectedIds.has(invoiceId);
    }

    toggleSelected(invoiceId: string, checked: boolean): void {
      if (checked) this.selectedIds.add(invoiceId);
      else this.selectedIds.delete(invoiceId);
    }

    isAllSelected(): boolean {
      if (!this.invoiceData || this.invoiceData.length === 0) return false;
      return this.invoiceData.every((i: any) => this.selectedIds.has(i._id));
    }

    toggleSelectAll(checked: boolean): void {
      if (!this.invoiceData) return;
      if (checked) {
        this.invoiceData.forEach((i: any) => this.selectedIds.add(i._id));
      } else {
        this.invoiceData.forEach((i: any) => this.selectedIds.delete(i._id));
      }
    }

    selectionCount(): number {
      return this.selectedIds.size;
    }

    /**
     * Bulk-toggle invoice status. Invoices currently support Draft <-> Sent;
     * we expose explicit Mark Sent / Mark Draft so the action is unambiguous.
     */
    bulkSetStatus(status: "Sent" | "Draft"): void {
      if (this.selectedIds.size === 0 || this.bulkBusy) return;
      if (!confirm(`Mark ${this.selectedIds.size} invoice(s) as ${status}?`)) return;
      this.bulkBusy = true;
      const ids = Array.from(this.selectedIds);
      const ops = ids.map((id) =>
        this.invoiceService.getOneInvoiceById(id).pipe(catchError(() => of(null))),
      );
      forkJoin(ops).subscribe({
        next: (invoices: any[]) => {
          const updates = invoices
            .filter((i: any) => !!i)
            .map((i: any) =>
              this.invoiceService
                .updateInvoice({ ...i, status })
                .pipe(catchError(() => of(null))),
            );
          if (updates.length === 0) {
            this.bulkBusy = false;
            return;
          }
          forkJoin(updates).subscribe({
            next: () => {
              this.alerts
                .open(`${updates.length} invoice(s) marked ${status}.`, {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.bulkBusy = false;
              this.loadInvoices();
            },
            error: () => {
              this.bulkBusy = false;
              this.alerts
                .open("Bulk update failed for some rows.", {
                  label: "Error",
                  appearance: "error",
                })
                .subscribe();
            },
          });
        },
        error: () => {
          this.bulkBusy = false;
        },
      });
    }

    exportSelectedToCsv(): void {
      if (!this.invoiceData || this.invoiceData.length === 0) return;
      const rows: any[] =
        this.selectedIds.size > 0
          ? this.invoiceData.filter((i: any) => this.selectedIds.has(i._id))
          : this.invoiceData;
      if (rows.length === 0) return;

      const headers = [
        "Invoice No",
        "Client Reg No",
        "Patient Name",
        "Contact No",
        "Amount",
        "Status",
        "Created Date",
      ];
      const escape = (val: any): string => {
        const s = (val ?? "").toString().replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };
      const lines = [headers.join(",")];
      for (const r of rows) {
        lines.push(
          [
            r.refNo || "",
            formatClientIdentifier(r.client) || "",
            r.client?.name || "",
            r.client?.guardianContactNo || "",
            r.amount ?? "",
            r.status || "",
            this.datePipe.transform(r.createdAt, "yyyy-MM-dd") || "",
          ]
            .map(escape)
            .join(","),
        );
      }
      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

}

